/**
 * AIChatPage — full-page AI chat view at /chat
 *
 * Uses the same ChatWindow component as the floating widget but in
 * a full-page layout with a session sidebar for history.
 */

import React, { useState, useCallback, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useAccount } from "../../context/AccountContext";
import { useChatContext } from "../../context/ChatContext";
import ChatWindow from "../../components/chat/ChatWindow";

// Runtime white-label config — set by nginx-mounted /app-config.js
const appName = (window.APP_CONFIG || {}).appName || "Fincrest Systems";

export default function AIChatPage() {
  const { user }  = useAuth();
  const isAdmin  = user?.user_type === "admin" || user?.user_type === "superadmin";
  const { selectedAccount } = useAccount();
  const {
    pendingMessage, clearPendingMessage,
    messages, loading, sessionId, sendMessage, startNewChat,
  } = useChatContext();
  const [initialDraft, setInitialDraft] = useState("");

  useEffect(() => {
    if (pendingMessage) {
      setInitialDraft(pendingMessage);
      clearPendingMessage();
    }
  }, [pendingMessage, clearPendingMessage]);

  const handleSend = useCallback((text) => {
    sendMessage(text, { accountUsername: selectedAccount || "" });
  }, [sendMessage, selectedAccount]);

  const handleNewChat = () => {
    startNewChat();
  };

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "calc(100vh - 60px)",
      background: "#f7f8fc",
    }}>
      {/* header bar */}
      <div style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "14px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20,
          }}>
            🤖
          </div>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{appName} AI</div>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>Platform support assistant</div>
          </div>
        </div>
        <button
          onClick={handleNewChat}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.4)",
            borderRadius: 8,
            color: "#fff",
            padding: "7px 16px",
            fontSize: 13,
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          + New Chat
        </button>
      </div>

      {/* chat area */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>
        <div style={{
          flex: 1,
          maxWidth: 800,
          margin: "0 auto",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          boxShadow: "0 0 20px rgba(0,0,0,0.06)",
        }}>
          <ChatWindow
            messages={messages}
            onSend={(text) => { setInitialDraft(""); handleSend(text); }}
            loading={loading}
            compact={false}
            sessionId={sessionId}
            initialDraft={initialDraft}
            isAdmin={isAdmin}
          />
        </div>
      </div>
    </div>
  );
}
